import { useEffect, useMemo, useState } from 'react'
import AppDateInput from '../../../components/AppDateInput'
import AppDropdown from '../../../components/AppDropdown'
import { fetchReportScheduleOptions } from '../api/reportsApi'
import { parseStoredUser } from '../../../utils/authSession'

const EMPTY_FILTERS = {
  companyIds: [],
  branchIds: [],
  departmentIds: [],
  userIds: [],
  projectIds: [],
  status: [],
  dateFrom: null,
  dateTo: null,
  search: '',
  page: 1,
  pageSize: 500,
}

export const DEFAULT_SCHEDULE_FORM = {
  module: '',
  reportKey: '',
  reportTitle: '',
  filters: { ...EMPTY_FILTERS },
  recurrenceType: 'WEEKLY',
  runDate: '',
  runTime: '08:00',
  weekday: '1',
  dayOfMonth: '1',
  recipientUserIds: [],
  recipientEmails: [],
  userToAdd: '',
  emailToAdd: '',
}

function toDateInputValue(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString().slice(0, 10)
}

export function normalizeScheduleForForm(schedule, defaultModule = '') {
  if (!schedule) {
    const activeEmail = parseStoredUser()?.email || ''
    return {
      ...DEFAULT_SCHEDULE_FORM,
      module: defaultModule || '',
      recipientEmails: activeEmail ? [activeEmail] : [],
    }
  }
  const rawEmails = Array.isArray(schedule.recipientEmails) ? [...schedule.recipientEmails] : []
  const activeEmail = parseStoredUser()?.email || ''
  if (!rawEmails.length && activeEmail) {
    rawEmails.push(activeEmail)
  }

  return {
    module: schedule.module || schedule.targetModule || defaultModule || '',
    reportKey: schedule.reportKey || schedule.id || '',
    reportTitle: schedule.reportTitle || schedule.title || '',
    filters: schedule.filters || { ...EMPTY_FILTERS },
    recurrenceType: schedule.recurrenceType || 'WEEKLY',
    runDate: toDateInputValue(schedule.oneTimeAt || schedule.nextRunAt || schedule.runDate),
    runTime: schedule.runTime || '08:00',
    weekday: schedule.weekday != null ? String(schedule.weekday) : '1',
    dayOfMonth: schedule.dayOfMonth != null ? String(schedule.dayOfMonth) : '1',
    recipientUserIds: Array.isArray(schedule.recipientUserIds) ? schedule.recipientUserIds.map(Number) : [],
    recipientEmails: rawEmails,
    userToAdd: '',
    emailToAdd: '',
  }
}

export function recurrenceLabel(value) {
  const key = String(value || '').toUpperCase()
  if (key === 'DAILY') return 'Daily Recurring'
  if (key === 'WEEKLY') return 'Weekly Recurring'
  if (key === 'MONTHLY') return 'Monthly Recurring'
  if (key === 'ONE_TIME') return 'One-time Trigger'
  return key || '-'
}

const RECURRENCE_CARDS = [
  { id: 'DAILY', label: 'Daily', icon: 'today', desc: 'Executes every day' },
  { id: 'WEEKLY', label: 'Weekly', icon: 'date_range', desc: 'Runs on specific weekday' },
  { id: 'MONTHLY', label: 'Monthly', icon: 'calendar_month', desc: 'Runs on calendar date' },
  { id: 'ONE_TIME', label: 'One-Time', icon: 'event_upcoming', desc: 'Single scheduled run' },
]

export default function ReportScheduleForm({
  initialValue = null,
  heading = 'Create Schedule',
  submitLabel = 'Create Schedule',
  onCancel,
  onSubmit,
  saving = false,
  scrollBody = false,
  bodyMaxHeightClass = '',
}) {
  const [options, setOptions] = useState({ timezone: 'Asia/Kolkata', modules: [], users: [], weekdayOptions: [] })
  const [loadingOptions, setLoadingOptions] = useState(false)
  const [form, setForm] = useState({ ...DEFAULT_SCHEDULE_FORM })
  const [error, setError] = useState('')

  const moduleOptions = useMemo(
    () => [
      { value: '', label: 'Select Module' },
      ...(options.modules || []).map((item) => ({
        value: item.id,
        label: item.id.replaceAll('-', ' ').toUpperCase(),
      })),
    ],
    [options.modules],
  )

  const reportOptions = useMemo(() => {
    const moduleRow = (options.modules || []).find((item) => item.id === form.module)
    const reports = moduleRow?.reports || []
    const list = [{ value: '', label: 'Select Report' }, ...reports.map((item) => ({ value: item.reportKey, label: item.title }))]
    if (form.reportKey && !list.some((item) => item.value === form.reportKey)) {
      list.push({ value: form.reportKey, label: form.reportTitle || form.reportKey })
    }
    return list
  }, [options.modules, form.module, form.reportKey, form.reportTitle])

  const weekdayOptions = useMemo(
    () => (options.weekdayOptions || []).map((item) => ({ value: String(item.value), label: item.label })),
    [options.weekdayOptions],
  )

  const userSelectionOptions = useMemo(
    () => [
      { value: '', label: 'Select Internal User...' },
      ...(options.users || []).map((item) => ({
        value: String(item.id),
        label: `${item.name}${item.email ? ` (${item.email})` : ''}`,
      })),
    ],
    [options.users],
  )

  const selectedUsers = useMemo(() => {
    const byId = new Map((options.users || []).map((item) => [Number(item.id), item]))
    return (form.recipientUserIds || []).map((id) => byId.get(Number(id))).filter(Boolean)
  }, [form.recipientUserIds, options.users])

  useEffect(() => {
    let active = true
    async function loadOptions() {
      setLoadingOptions(true)
      try {
        const data = await fetchReportScheduleOptions()
        if (!active) return
        const loadedModules = Array.isArray(data?.modules) ? data.modules : []
        setOptions({
          timezone: data?.timezone || 'Asia/Kolkata',
          modules: loadedModules,
          users: Array.isArray(data?.users) ? data.users : [],
          weekdayOptions: Array.isArray(data?.weekdayOptions) ? data.weekdayOptions : [],
        })
        // Auto-infer module if reportKey is present but module is empty
        setForm((prev) => {
          if (prev.reportKey && (!prev.module || prev.module === '')) {
            const foundMod = loadedModules.find((m) =>
              (m.reports || []).some((r) => r.reportKey === prev.reportKey)
            )
            if (foundMod) {
              const matchedReport = (foundMod.reports || []).find((r) => r.reportKey === prev.reportKey)
              return {
                ...prev,
                module: foundMod.id,
                reportTitle: prev.reportTitle || matchedReport?.title || prev.reportKey,
              }
            }
          }
          return prev
        })
      } catch (requestError) {
        if (!active) return
        setError(requestError?.response?.data?.detail?.message || 'Failed to load schedule options.')
      } finally {
        if (active) setLoadingOptions(false)
      }
    }
    loadOptions()
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    setForm(normalizeScheduleForForm(initialValue))
    setError('')
  }, [initialValue])

  function setFormField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function onSelectModule(modId) {
    setForm((prev) => {
      const modRow = (options.modules || []).find((m) => m.id === modId)
      const currentInMod = (modRow?.reports || []).some((r) => r.reportKey === prev.reportKey)
      const firstReport = modRow?.reports?.[0]
      return {
        ...prev,
        module: modId,
        reportKey: currentInMod ? prev.reportKey : (firstReport?.reportKey || ''),
        reportTitle: currentInMod ? prev.reportTitle : (firstReport?.title || ''),
      }
    })
  }

  function onSelectReport(reportKey) {
    let matchedModule = form.module
    let matchedTitle = form.reportTitle

    for (const mod of options.modules || []) {
      const match = (mod.reports || []).find((r) => r.reportKey === reportKey)
      if (match) {
        matchedModule = mod.id
        matchedTitle = match.title
        break
      }
    }

    setForm((prev) => ({
      ...prev,
      module: matchedModule || prev.module,
      reportKey,
      reportTitle: matchedTitle || prev.reportTitle,
    }))
  }

  function addRecipientUser() {
    const userId = Number(form.userToAdd || 0)
    if (!userId) return
    setForm((prev) => ({
      ...prev,
      recipientUserIds: prev.recipientUserIds.includes(userId) ? prev.recipientUserIds : [...prev.recipientUserIds, userId],
      userToAdd: '',
    }))
  }

  function removeRecipientUser(userId) {
    setForm((prev) => ({
      ...prev,
      recipientUserIds: prev.recipientUserIds.filter((id) => Number(id) !== Number(userId)),
    }))
  }

  function addRecipientEmail() {
    const email = String(form.emailToAdd || '').trim().toLowerCase()
    if (!email) return
    if (!email.includes('@') || !email.includes('.')) {
      setError('Please enter a valid email address.')
      return
    }
    setError('')
    setForm((prev) => ({
      ...prev,
      recipientEmails: prev.recipientEmails.includes(email) ? prev.recipientEmails : [...prev.recipientEmails, email],
      emailToAdd: '',
    }))
  }

  function removeRecipientEmail(email) {
    setForm((prev) => ({
      ...prev,
      recipientEmails: prev.recipientEmails.filter((item) => item !== email),
    }))
  }

  async function submit() {
    setError('')
    const payload = {
      module: form.module,
      reportKey: form.reportKey,
      reportTitle: form.reportTitle || undefined,
      filters: form.filters || { ...EMPTY_FILTERS },
      recurrenceType: form.recurrenceType,
      runDate: form.recurrenceType === 'ONE_TIME' ? form.runDate : null,
      runTime: form.runTime,
      weekday: form.recurrenceType === 'WEEKLY' ? Number(form.weekday) : null,
      dayOfMonth: form.recurrenceType === 'MONTHLY' ? Number(form.dayOfMonth) : null,
      recipientUserIds: form.recipientUserIds,
      recipientEmails: form.recipientEmails,
    }

    if (!payload.module || !payload.reportKey) {
      setError('Module and report are required.')
      return
    }
    if (payload.recurrenceType === 'ONE_TIME' && !payload.runDate) {
      setError('Run date is required for one-time schedules.')
      return
    }
    if (!payload.recipientUserIds.length && !payload.recipientEmails.length) {
      setError('Add at least one recipient email or internal user.')
      return
    }

    try {
      await onSubmit?.(payload)
    } catch (requestError) {
      setError(requestError?.response?.data?.detail?.message || 'Failed to save schedule.')
    }
  }

  const bodyScrollClass = scrollBody ? 'flex-1 min-h-0 overflow-y-auto overscroll-contain pr-1.5' : ''
  const totalRecipients = (form.recipientUserIds?.length || 0) + (form.recipientEmails?.length || 0)

  const cadenceSummary = useMemo(() => {
    const timeStr = form.runTime || '08:00'
    const tzStr = options.timezone || 'Asia/Kolkata'
    if (form.recurrenceType === 'DAILY') {
      return `Daily at ${timeStr} (${tzStr})`
    }
    if (form.recurrenceType === 'WEEKLY') {
      const match = weekdayOptions.find((w) => w.value === String(form.weekday))
      const dayName = match?.label || 'Monday'
      return `Weekly on ${dayName} at ${timeStr} (${tzStr})`
    }
    if (form.recurrenceType === 'MONTHLY') {
      return `Monthly on day ${form.dayOfMonth || 1} at ${timeStr} (${tzStr})`
    }
    if (form.recurrenceType === 'ONE_TIME') {
      return `One-time on ${form.runDate || 'selected date'} at ${timeStr} (${tzStr})`
    }
    return ''
  }, [form.recurrenceType, form.runTime, form.weekday, form.dayOfMonth, form.runDate, weekdayOptions, options.timezone])

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className={`space-y-4 ${bodyScrollClass}`}>
        {/* ── Recurrence Frequency Cards (Top Full Width) ── */}
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-on-surface block mb-2">
            Dispatch Recurrence Frequency <span className="text-rose-500">*</span>
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {RECURRENCE_CARDS.map((card) => {
              const active = form.recurrenceType === card.id
              return (
                <button
                  key={card.id}
                  type="button"
                  onClick={() => setFormField('recurrenceType', card.id)}
                  className={`relative flex flex-col items-center justify-center p-3 rounded-2xl border text-center transition-all cursor-pointer ${
                    active
                      ? 'border-2 border-primary bg-primary/10 text-primary shadow-xs font-bold'
                      : 'border-outline-variant/30 bg-surface-container-low text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
                  }`}
                >
                  {active ? (
                    <span className="absolute top-2 right-2 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-on-primary">
                      <span className="material-symbols-outlined text-[11px] font-bold">check</span>
                    </span>
                  ) : null}
                  <span className={`material-symbols-outlined text-2xl mb-1 ${active ? 'text-primary' : 'text-on-surface-variant'}`}>
                    {card.icon}
                  </span>
                  <span className="text-xs font-bold">{card.label}</span>
                  <span className="text-[10px] opacity-75 mt-0.5">{card.desc}</span>
                </button>
              )
            })}
          </div>
        </div>

        {/* ── 2-Column Responsive Dashboard Layout ── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4.5 items-start">
          {/* ── Left Column: Target Report & Execution Timing (6 Cols) ── */}
          <div className="lg:col-span-6 flex flex-col gap-4">
            {/* Card 1: Target Report & Subject */}
            <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-low/40 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-sm text-primary">assessment</span>
                <span className="text-[11px] font-bold uppercase tracking-wider text-on-surface">Target Report & Subject Line</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Module</label>
                  <AppDropdown
                    value={form.module}
                    onChange={(value) => onSelectModule(String(value))}
                    options={moduleOptions}
                    triggerClassName="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 py-2 text-xs font-bold text-on-surface"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Report Definition</label>
                  <AppDropdown
                    value={form.reportKey}
                    onChange={(value) => onSelectReport(String(value))}
                    options={reportOptions}
                    triggerClassName="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 py-2 text-xs font-bold text-on-surface"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">
                  Dispatch Title / Email Subject
                </label>
                <input
                  type="text"
                  value={form.reportTitle}
                  onChange={(event) => setFormField('reportTitle', event.target.value)}
                  placeholder="e.g. Weekly Operational Summary"
                  className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3.5 py-2 text-xs font-bold text-on-surface focus:outline-none focus:border-primary"
                />
              </div>
            </div>

            {/* Card 2: Execution Cadence & Timing */}
            <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-low/40 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-sm text-primary">alarm</span>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-on-surface">Execution Timing</span>
                </div>
                <span className="inline-flex items-center gap-1 rounded-lg bg-surface-container-high px-2 py-0.5 text-[10px] font-bold text-on-surface-variant">
                  <span className="material-symbols-outlined text-xs">public</span>
                  {options.timezone || 'Asia/Kolkata'}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Run Time</label>
                  <input
                    type="time"
                    value={form.runTime}
                    onChange={(event) => setFormField('runTime', event.target.value)}
                    className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 py-2 text-xs font-bold text-on-surface focus:outline-none focus:border-primary"
                  />
                </div>

                {form.recurrenceType === 'WEEKLY' ? (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Weekday</label>
                    <AppDropdown
                      value={form.weekday}
                      onChange={(value) => setFormField('weekday', String(value))}
                      options={weekdayOptions}
                      triggerClassName="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 py-2 text-xs font-bold text-on-surface"
                    />
                  </div>
                ) : null}

                {form.recurrenceType === 'MONTHLY' ? (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Day of Month (1 - 31)</label>
                    <input
                      type="number"
                      min={1}
                      max={31}
                      value={form.dayOfMonth}
                      onChange={(event) => setFormField('dayOfMonth', event.target.value)}
                      className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 py-2 text-xs font-bold text-on-surface focus:outline-none focus:border-primary"
                    />
                  </div>
                ) : null}

                {form.recurrenceType === 'ONE_TIME' ? (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Target Date</label>
                    <AppDateInput
                      value={form.runDate}
                      onChange={(value) => setFormField('runDate', value)}
                      inputClassName="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 py-2 text-xs font-bold text-on-surface focus:outline-none focus:border-primary"
                    />
                  </div>
                ) : null}

                {form.recurrenceType === 'DAILY' ? (
                  <div className="flex flex-col justify-center rounded-xl bg-surface-container-high/60 p-2.5 border border-outline-variant/20">
                    <span className="text-[10px] font-bold text-primary uppercase">Recurrence Note</span>
                    <span className="text-[11px] text-on-surface-variant mt-0.5">Executes every single calendar day at {form.runTime || '08:00'}.</span>
                  </div>
                ) : null}
              </div>

              <div className="flex items-center gap-1.5 text-[10px] text-on-surface-variant/80 pt-0.5">
                <span className="material-symbols-outlined text-xs text-primary">sync</span>
                <span>Handled by background daemon and dispatched asynchronously via ThreadPool.</span>
              </div>
            </div>
          </div>

          {/* ── Right Column: Delivery Recipients (Internal Users + External Emails) (6 Cols) ── */}
          <div className="lg:col-span-6 flex flex-col gap-4">
            <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-low/40 p-4 space-y-3.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-sm text-primary">outgoing_mail</span>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-on-surface">Delivery Recipients</span>
                </div>
                <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 border border-primary/20 px-2.5 py-0.5 text-[10px] font-bold text-primary">
                  {totalRecipients} Recipient{totalRecipients === 1 ? '' : 's'}
                </span>
              </div>

              {/* Sub-section A: Internal ERP Users */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider flex items-center gap-1">
                    <span className="material-symbols-outlined text-xs">group</span>
                    Internal ERP Users
                  </label>
                  <span className="text-[10px] font-semibold text-on-surface-variant">
                    {selectedUsers.length} Added
                  </span>
                </div>

                <div className="grid grid-cols-[1fr_auto] gap-2">
                  <AppDropdown
                    value={form.userToAdd}
                    onChange={(value) => setFormField('userToAdd', String(value))}
                    options={userSelectionOptions}
                    triggerClassName="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 py-2 text-xs font-bold text-on-surface"
                  />
                  <button
                    type="button"
                    onClick={addRecipientUser}
                    className="px-3.5 py-2 bg-primary/10 text-primary border border-primary/20 rounded-xl text-xs font-bold hover:bg-primary/20 transition-colors flex items-center gap-1 cursor-pointer"
                  >
                    <span className="material-symbols-outlined text-sm">add</span>
                    Add User
                  </button>
                </div>

                {selectedUsers.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto pr-1">
                    {selectedUsers.map((user) => (
                      <span
                        key={user.id}
                        className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 border border-primary/20 pl-2.5 pr-1.5 py-0.5 text-xs font-bold text-primary"
                      >
                        <span className="material-symbols-outlined text-xs">person</span>
                        <span>{user.name}</span>
                        <button
                          type="button"
                          onClick={() => removeRecipientUser(user.id)}
                          className="flex h-3.5 w-3.5 items-center justify-center rounded-full hover:bg-primary/20 cursor-pointer"
                          aria-label={`Remove ${user.name}`}
                        >
                          <span className="material-symbols-outlined text-[10px]">close</span>
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-[10px] text-on-surface-variant italic">No internal system users added yet.</p>
                )}
              </div>

              <div className="border-t border-surface-container-high/60 my-1"></div>

              {/* Sub-section B: External Email Addresses */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider flex items-center gap-1">
                    <span className="material-symbols-outlined text-xs">alternate_email</span>
                    External Email Addresses
                  </label>
                  <span className="text-[10px] font-semibold text-on-surface-variant">
                    {form.recipientEmails.length} Added
                  </span>
                </div>

                <div className="grid grid-cols-[1fr_auto] gap-2">
                  <input
                    type="email"
                    value={form.emailToAdd}
                    onChange={(event) => setFormField('emailToAdd', event.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        addRecipientEmail()
                      }
                    }}
                    placeholder="e.g. auditor@external.com"
                    className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 py-2 text-xs font-bold text-on-surface focus:outline-none focus:border-primary"
                  />
                  <button
                    type="button"
                    onClick={addRecipientEmail}
                    className="px-3.5 py-2 bg-primary text-on-primary rounded-xl text-xs font-bold hover:opacity-90 transition-opacity flex items-center gap-1 cursor-pointer"
                  >
                    <span className="material-symbols-outlined text-sm">add</span>
                    Add Email
                  </button>
                </div>

                {form.recipientEmails.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto pr-1">
                    {form.recipientEmails.map((email) => (
                      <span
                        key={email}
                        className="inline-flex items-center gap-1.5 rounded-full bg-surface-container-high border border-outline-variant/30 pl-2.5 pr-1.5 py-0.5 text-xs font-bold text-on-surface"
                      >
                        <span className="material-symbols-outlined text-xs text-on-surface-variant">mail</span>
                        <span>{email}</span>
                        <button
                          type="button"
                          onClick={() => removeRecipientEmail(email)}
                          className="flex h-3.5 w-3.5 items-center justify-center rounded-full hover:bg-surface-container-highest cursor-pointer"
                          aria-label={`Remove ${email}`}
                        >
                          <span className="material-symbols-outlined text-[10px]">close</span>
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-[10px] text-on-surface-variant italic">No external email addresses added yet.</p>
                )}
              </div>

              {/* Delivery notice */}
              <div className="rounded-xl bg-surface-container-high/40 p-2.5 border border-outline-variant/10 text-[10px] text-on-surface-variant flex items-center gap-1.5">
                <span className="material-symbols-outlined text-xs text-primary">verified_user</span>
                <span>Delivered with Tenant SMTP and logged under Outgoing Delivery Audit Logs.</span>
              </div>
            </div>
          </div>
        </div>

        {error ? (
          <div className="flex items-center gap-2 p-3 rounded-xl text-xs font-semibold bg-rose-50 text-rose-800 border border-rose-200 animate-in fade-in">
            <span className="material-symbols-outlined text-base shrink-0">error</span>
            <span>{error}</span>
          </div>
        ) : null}
      </div>

      {/* ── Form Action Footer ── */}
      <div className="shrink-0 mt-3 pt-3 border-t border-surface-container flex items-center justify-between bg-surface-container-lowest">
        <div className="flex items-center gap-2 text-xs text-on-surface-variant font-medium">
          <span className="material-symbols-outlined text-sm text-primary">mark_email_read</span>
          <span>Total Delivery Targets: <strong className="text-on-surface font-bold">{totalRecipients}</strong></span>
          {cadenceSummary ? (
            <span className="hidden sm:inline-flex items-center gap-1 rounded-full bg-surface-container-high px-2.5 py-0.5 text-[10px] font-bold text-on-surface-variant">
              {cadenceSummary}
            </span>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          {onCancel ? (
            <button
              type="button"
              className="px-3.5 py-1.5 rounded-[8px] text-xs font-bold text-on-surface-variant hover:bg-surface-container transition-colors cursor-pointer"
              onClick={onCancel}
              disabled={saving || loadingOptions}
            >
              Cancel
            </button>
          ) : null}
          <button
            type="button"
            onClick={submit}
            disabled={saving || loadingOptions}
            className="flex items-center gap-1 px-4 py-1.5 rounded-[8px] text-xs font-bold bg-primary text-on-primary shadow-sm hover:opacity-90 transition-opacity disabled:opacity-50 cursor-pointer"
          >
            {saving ? (
              <>
                <div className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <span>{submitLabel}</span>
                <span className="material-symbols-outlined text-sm">chevron_right</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

